import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { useProjectAnalyticsGroup } from "@base/model/hooks";
import { AppLink } from "@base/ui/AppLink";
import { BottomNav } from "@base/ui/BottomNav";
import { PullToRefreshIndicator } from "@base/ui/PullToRefreshScrollView/PullToRefreshIndicator";
import { usePullToRefresh } from "@base/ui/PullToRefreshScrollView/usePullToRefresh";
import { useSafeAreaInset } from "@base/ui/SafeArea/useSafeAreaInset";
import { invalidateDailyLogSearch } from "@features/project-detail/model/dailyLogSearchQueries";
import { refreshActiveDailyLogSearch } from "@features/project-detail/model/dailyLogSearchRefresh";
import { projectDetailQueryOptions } from "@features/project-detail/model/useProjectDetailQuery";
import { NewDailyLogButton } from "@features/project-detail/ui/NewDailyLogButton";
import { ProjectDocumentsTabContent } from "@features/project-detail/ui/ProjectDocumentsTabContent";
import { ProjectHeroImage } from "@features/project-detail/ui/ProjectHeroImage";
import { ProjectOverviewTabContent } from "@features/project-detail/ui/ProjectOverviewTabContent";
import { ProjectPageHeader } from "@features/project-detail/ui/ProjectPageHeader";
import { ProjectTabNav } from "@features/project-detail/ui/ProjectTabNav";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

const PROJECT_TABS = ["overview", "documents"] as const;

/**
 * TanStack Router 기본 파서는 값을 JSON으로 먼저 해석하므로 `?q=123`·`?contractors=7`은 number로 들어온다.
 * 검색 조건 모듈에서 문자열로 검증한다. 해석할 수 없는 값은 버려 화면 전체를 잃지 않게 한다.
 */
const searchTextParam = z.union([z.string(), z.number(), z.boolean()]).transform(String).optional().catch(undefined);
// ID 하나는 number 그대로 둬야 직렬화 때 `contractors=%227%22`처럼 따옴표가 붙지 않는다.
const searchIdListParam = z
  .union([z.string(), z.number(), z.boolean().transform(String)])
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  tab: z.enum(PROJECT_TABS).optional().default("overview").catch("overview"),
  q: searchTextParam,
  from: searchTextParam,
  to: searchTextParam,
  contractors: searchIdListParam,
  tags: searchIdListParam,
});

export const Route = createFileRoute("/_authenticated/projects/$projectId/")({
  validateSearch: searchSchema,
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { bottom } = useSafeAreaInset();
  const { projectId } = Route.useParams();
  const { tab, ...dailyLogSearchParams } = Route.useSearch();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  useProjectAnalyticsGroup(projectId);

  useEffect(
    function trackProjectDetailView() {
      EventLogger.view(CLIENT_EVENT.PROJECT_DETAIL_VIEW, { project_id: projectId });
    },
    [projectId],
  );

  // 탭은 URL 검색 파라미터라 뒤로가기·새로고침으로도 바뀐다.
  // 클릭이 아니라 `tab` 값 자체를 추적해야 실제로 본 탭이 집계된다.
  useEffect(
    function trackProjectTabView() {
      EventLogger.view(CLIENT_EVENT.PROJECT_TAB_VIEW, { project_id: projectId, tab });
    },
    [projectId, tab],
  );

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["documents", "infinite", Number(projectId)] });
    await invalidateDailyLogSearch(queryClient, projectId);
    await refreshActiveDailyLogSearch(projectId);
    await queryClient.invalidateQueries({ queryKey: projectDetailQueryOptions(projectId).queryKey });
  };

  const { containerRef, pullDistance, isRefreshing, isPulling } = usePullToRefresh({ onRefresh: handleRefresh });

  return (
    <div
      // 모바일은 하단 내비 높이만큼 비운다. PC는 하단 내비가 없고 좌우 여백은 shell이 준다.
      className="flex relative flex-col p-4 h-dvh pb-[var(--bottom-nav-space)] md:px-0 md:pb-6"
      style={{ "--bottom-nav-space": `calc(92px + ${bottom}px)` } as React.CSSProperties}
      ref={containerRef}
    >
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} isPulling={isPulling} />

      <ProjectPageHeader
        projectId={projectId}
        mobileTitle="Projects"
        mobileBackLink={
          <AppLink to="/" aria-label="Home">
            <ChevronLeft />
          </AppLink>
        }
        actions={<NewDailyLogButton projectId={projectId} />}
      />

      <div className="md:hidden">
        <ProjectHeroImage />
      </div>

      <ProjectTabNav projectId={projectId} activeTab={tab} documentsSearch={dailyLogSearchParams} />

      {tab === "overview" && (
        <div className="flex-1 pt-4 pb-32 md:pb-8">
          <ProjectOverviewTabContent documentsSearch={dailyLogSearchParams} />
        </div>
      )}

      {tab === "documents" && (
        <div className="relative flex-1">
          <ProjectDocumentsTabContent
            projectId={projectId}
            searchParams={dailyLogSearchParams}
            onSearchParamsChange={(params) =>
              navigate({
                to: "/projects/$projectId",
                params: { projectId },
                search: { tab: "documents", ...params },
                replace: true,
                // 조건 적용마다 화면 전환 애니메이션이 돌면 이전 목록이 겹쳐 보인다.
                viewTransition: false,
              })
            }
          />
        </div>
      )}

      <BottomNav />
    </div>
  );
}
